import { useCallback } from 'react';
import { useSignal } from '@funtech-int/rival-react';

import {
    ContentMarkType,
    PlayerImpressionEntity,
} from '@kinopoisk-int/evgen-web-events/web/named_enums';
import { playerMovieImpressionMarked } from '@kinopoisk-int/evgen-web-events/web/player';

import { usePlayerInstance } from '@/scenarios/legacy-player/lib/contexts/instance';
import { useLogger } from '@/shared/hooks/useLogger';
import { Dependencies } from '@/scenarios/legacy-player/hooks/analytics/use-dependencies';
import { useEventCallback } from '@/scenarios/legacy-player/hooks/analytics/use-event-callback';
import { useMovieUGC } from '@/shared/hooks/useMovieUGC';
import { useMoviePlannedToWatch } from '@/shared/hooks/useMoviePlannedToWatch';

interface UiParams {
    mainUuid: string;
    promotedMovieTitle?: string | null;
}

interface Params {
    uiParams: UiParams;
    dependencies: Dependencies;
    callbackParams: [{ vsid: string; markType: ContentMarkType; uuid: string }];
}

interface FilmFavoriteToggleParams {
    contentId: string;
    promotedMovieid: number;
}

const useLogToggleFilmFavoriteButtonActivation = (uiParams: UiParams) =>
    useEventCallback({
        event: playerMovieImpressionMarked,
        uiParams,
        eventParamsBuilder: prerollContent,
    });

const prerollContent: (params: Params) => Parameters<typeof playerMovieImpressionMarked>[1] = ({
    uiParams: { mainUuid, promotedMovieTitle },
    callbackParams: [{ vsid, markType, uuid }],
}) => ({
    markType,
    entityType: PlayerImpressionEntity.Preroll,
    uuid,
    title: promotedMovieTitle ?? '-',
    vsid,
    mainUuid,
});

export const useFilmFavoriteToggleWithAnalytics = (params: FilmFavoriteToggleParams) => {
    const { contentId, promotedMovieid } = params;

    const logger = useLogger();

    const { title: promotedMovieTitle, contentId: promotedContentId } = useMoviePlannedToWatch({
        movieId: promotedMovieid,
    });

    const movieUGC = useMovieUGC(promotedMovieid);
    const isPlannedToWatch = useSignal(movieUGC.plannedToWatch);

    const logToggleFilmFavorite = useLogToggleFilmFavoriteButtonActivation({
        mainUuid: contentId,
        promotedMovieTitle,
    });

    const { vsid } = usePlayerInstance() || {};

    const onFilmFavoriteToggleWithAnalylics = useCallback(() => {
        if (!promotedContentId || typeof promotedContentId !== 'string') {
            logger.error(
                {
                    promotedContentId,
                    isPlannedToWatch,
                    vsid,
                    promotedMovieid,
                    type: 'film',
                },
                'Tried to add title to watch later with invalid promotedMovieid'
            );
            return;
        }

        logToggleFilmFavorite({
            vsid: vsid || '',
            markType: isPlannedToWatch ? ContentMarkType.Unbookmarked : ContentMarkType.Bookmarked,
            uuid: promotedMovieid.toString(),
        });
        movieUGC.togglePlannedToWatch({ toastViewVariant: 'TOP' });
    }, [
        promotedContentId,
        logToggleFilmFavorite,
        vsid,
        isPlannedToWatch,
        promotedMovieid,
        movieUGC,
        logger,
    ]);

    return { onFilmFavoriteToggleWithAnalylics, isPlannedToWatch, promotedContentId };
};
